import articlesHelpEnUs from '../../data/knowledge/articles.help.en-us.json';
import articlesHelpFrFr from '../../data/knowledge/articles.help.fr-fr.json';
import articlesHeroEnUs from '../../data/knowledge/articles.hero.en-us.json';
import articlesHeroFrFr from '../../data/knowledge/articles.hero.fr-fr.json';
import categoriesHelpEnUs from '../../data/knowledge/categories.help.en-us.json';
import categoriesHelpFrFr from '../../data/knowledge/categories.help.fr-fr.json';
import categoriesHeroEnUs from '../../data/knowledge/categories.hero.en-us.json';
import categoriesHeroFrFr from '../../data/knowledge/categories.hero.fr-fr.json';
import { KnowledgeLocale, resolveKnowledgeLocale } from './knowledgeCopy';
import { KnowledgeArticle, KnowledgeCategory, KnowledgeChannel } from './types';
import {
  applyKnowledgeArticleAudit,
  applyKnowledgeCategoriesAudit,
  isHelpArticleRemoved,
  isHeroArticleMovedToHelp,
  isHeroArticleRemoved,
} from './knowledgeAudit';

const articlesByChannel: Record<KnowledgeChannel, Record<KnowledgeLocale, KnowledgeArticle[]>> = {
  hero: {
    'en-gb': articlesHeroEnUs as KnowledgeArticle[],
    'fr-fr': articlesHeroFrFr as KnowledgeArticle[],
  },
  help: {
    'en-gb': articlesHelpEnUs as KnowledgeArticle[],
    'fr-fr': articlesHelpFrFr as KnowledgeArticle[],
  },
};

const categoriesByChannel: Record<KnowledgeChannel, Record<KnowledgeLocale, KnowledgeCategory[]>> = {
  hero: {
    'en-gb': categoriesHeroEnUs as KnowledgeCategory[],
    'fr-fr': categoriesHeroFrFr as KnowledgeCategory[],
  },
  help: {
    'en-gb': categoriesHelpEnUs as KnowledgeCategory[],
    'fr-fr': categoriesHelpFrFr as KnowledgeCategory[],
  },
};

function normKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function shouldIncludeArticle(article: KnowledgeArticle, channel: KnowledgeChannel): boolean {
  const key = normKey(article.urlName);

  if (channel === 'help' && isHelpArticleRemoved(article.urlName)) {
    return false;
  }

  if (channel === 'hero') {
    if (isHeroArticleRemoved(article.urlName) || isHeroArticleMovedToHelp(article.urlName)) {
      return false;
    }
  }

  if (channel === 'help' && isHeroArticleMovedToHelp(article.urlName)) {
    return true;
  }

  if (channel === 'help' && key.startsWith('urbanrewards')) {
    return false;
  }

  return true;
}

function finalizeArticles(
  articles: KnowledgeArticle[],
  channel: KnowledgeChannel,
  locale: KnowledgeLocale
): KnowledgeArticle[] {
  return articles
    .filter((article) => shouldIncludeArticle(article, channel))
    .map((article) => applyKnowledgeArticleAudit(article, channel, locale))
    .sort((a, b) => a.title.localeCompare(b.title));
}

export function getKnowledgeArticles(locale: string, channel: KnowledgeChannel): KnowledgeArticle[] {
  const resolved = resolveKnowledgeLocale(locale);
  const primary = (articlesByChannel[channel][resolved] || []).filter((article) => {
    if (channel !== 'help') {
      return true;
    }
    return !isHelpArticleRemoved(article.urlName);
  });

  if (channel === 'help') {
    const movedFromHero = (articlesByChannel.hero[resolved] || []).filter((article) =>
      isHeroArticleMovedToHelp(article.urlName)
    );
    const merged = new Map<string, KnowledgeArticle>();
    for (const article of [...primary, ...movedFromHero]) {
      merged.set(normKey(article.urlName), article);
    }
    return finalizeArticles([...merged.values()], channel, resolved);
  }

  return finalizeArticles(primary, channel, resolved);
}

export function getKnowledgeCategories(locale: string, channel: KnowledgeChannel): KnowledgeCategory[] {
  const resolved = resolveKnowledgeLocale(locale);
  const categories = categoriesByChannel[channel][resolved] || [];
  return applyKnowledgeCategoriesAudit(categories, channel, resolved);
}

export function getKnowledgeArticleByUrlName(
  urlName: string,
  locale: string,
  channel: KnowledgeChannel
): KnowledgeArticle | undefined {
  const normalized = decodeURIComponent(urlName);
  return getKnowledgeArticles(locale, channel).find(
    (article) => article.urlName.toLowerCase() === normalized.toLowerCase()
  );
}

export function getKnowledgeArticleBySlug(
  slug: string,
  locale: string,
  channel: KnowledgeChannel
): KnowledgeArticle | undefined {
  const key = normKey(slug);
  return getKnowledgeArticles(locale, channel).find((article) => normKey(article.urlName) === key);
}

export function getAllKnowledgeArticlePaths(
  locale: string,
  channel: KnowledgeChannel
): { urlName: string }[] {
  return getKnowledgeArticles(locale, channel).map(({ urlName }) => ({ urlName }));
}

export function getKnowledgeIndexLink(locale: string, channel: KnowledgeChannel): string {
  const resolved = resolveKnowledgeLocale(locale);
  return `/${resolved}/${channel}`;
}

export function getKnowledgeCategoryLink(
  locale: string,
  channel: KnowledgeChannel,
  categorySlug: string
): string {
  const resolved = resolveKnowledgeLocale(locale);
  return `/${resolved}/${channel}/category/${categorySlug}`;
}

export function getKnowledgeArticleLink(
  locale: string,
  channel: KnowledgeChannel,
  urlName: string
): string {
  const resolved = resolveKnowledgeLocale(locale);
  return `/${resolved}/${channel}/article/${urlName}`;
}

export function getKnowledgeCategoryBySlug(
  slug: string,
  locale: string,
  channel: KnowledgeChannel
): KnowledgeCategory | undefined {
  const normalized = slug.toLowerCase();
  return getKnowledgeCategories(locale, channel).find(
    (category) => category.slug.toLowerCase() === normalized
  );
}

export function getArticleCategoryNames(
  articleUrlName: string,
  locale: string,
  channel: KnowledgeChannel
): string[] {
  const key = normKey(articleUrlName);
  return getKnowledgeCategories(locale, channel)
    .filter((category) =>
      category.articleSlugs.some((slug) => normKey(slug) === key || slug === articleUrlName)
    )
    .map((category) => category.name);
}
